import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { aiService } from '../../services/aiService';

const CON_TYPES = [
  { id:'recipe',    label:'Recipe',     icon:'☕', angles:['Morning ritual','Cold brew guide','Seasonal recipe','Hotel-inspired brew','Quick weekday coffee'] },
  { id:'lifestyle', label:'Lifestyle',  icon:'✨', angles:['Monday motivation','Coffee moment','Weekend vibe','Work from café','Slow morning'] },
  { id:'quote',     label:'Quote',      icon:'💬', angles:['Coffee wisdom','Sustainability thought','Inspirational','Relatable humor','Monday mood'] },
  { id:'education', label:'Education',  icon:'🌱', angles:['Sustainability fact','Origin story','Brewing science','Why compostable','Rainforest Alliance'] },
  { id:'promo',     label:'Promotion',  icon:'🎁', angles:['Product highlight','Limited edition','Bundle deal','Seasonal launch','Gift idea'] },
];

const CON_PLATFORMS = {
  instagram: { label:'Instagram', icon:'📸', color:'#e1306c', tip:'Best for visuals, recipes, lifestyle. 8–12 hashtags. 150–220 words.' },
  facebook:  { label:'Facebook',  icon:'👥', color:'#1877f2', tip:'Great for stories and community. Slightly longer. No hashtags needed.' },
  blog:      { label:'Blog',      icon:'📝', color:'#2e7d32', tip:'SEO-optimised long form. 600–900 words. Use for recipes and guides.' },
};

export default function ConsumerCreate({ prefillIdea }) {
  const { addConPost, updateConPost, removeConPost, conPosts, anthropicKey, anthropicKeyReady, voiceState, logActivity } = useApp();

  const [selType,      setSelType]      = useState('recipe');
  const [selPlatform,  setSelPlatform]  = useState('instagram');
  const [context,      setContext]      = useState(prefillIdea ? prefillIdea.title + (prefillIdea.notes ? '\n'+prefillIdea.notes : '') : '');
  const [schedDate,    setSchedDate]    = useState('');
  const [generating,   setGenerating]   = useState(false);
  const [generated,    setGenerated]    = useState(null);
  const [angle,        setAngle]        = useState(CON_TYPES[0].angles[0]);

  const currentType = CON_TYPES.find(t => t.id === selType) || CON_TYPES[0];
  const currentPlat = CON_PLATFORMS[selPlatform];

  async function generate() {
    if (!anthropicKeyReady) { alert('Add your Anthropic API key in Settings → API Keys first.'); return; }
    setGenerating(true);
    setGenerated(null);

    try {
      const voiceBlock = voiceState?.description ? `Brand voice: ${voiceState.description}` : '';
      const prompt = `You write content for Tayst Coffee — premium sustainable coffee. Writing for consumers.
${voiceBlock}
Post type: ${currentType.label}. Angle: ${angle}. Platform: ${currentPlat.label}.
${context ? 'Theme/context: '+context+'.' : ''}
${selPlatform === 'blog'
  ? `Write an SEO blog post (600–900 words): engaging H1, intro hook, 2–3 H2 sections, soft Tayst close, SEO meta description. Use markdown.`
  : `Write a ${selPlatform==='instagram'?'150–220':'180–280'} word post. Warm, approachable. ${selPlatform==='instagram'?'End with 8–12 hashtags.':''}`
}
Return ONLY the post.`;

      const text = await aiService.rawComplete({ message: prompt, maxTokens: 2048, anthropicKey });

      const post = await addConPost({
        type:          currentType.id,
        title:         angle || currentType.label,
        platform:      selPlatform,
        world:         'consumer',
        content:       text,
        status:        schedDate ? 'scheduled' : 'draft',
        scheduled_date:schedDate || null,
      });
      logActivity(`Generated ${currentType.label} for ${currentPlat.label}`);
      setGenerated(post);
    } catch (e) {
      alert('Generation failed: ' + (e.message || String(e)));
    }
    setGenerating(false);
  }

  async function regenerate() {
    if (generated) await removeConPost(generated.id).catch(console.error);
    setGenerated(null);
    generate();
  }

  const drafts = conPosts.filter(p => p.status === 'draft');

  return (
    <div style={{ overflowY:'auto', padding:'20px 24px' }}>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, marginBottom:4 }}>Create Consumer Post</div>
      <div style={{ fontSize:11, color:'#9ca3af', marginBottom:16 }}>Choose type, platform, and angle — then generate</div>

      <div style={{ display:'grid', gridTemplateColumns:'320px 1fr', gap:16, alignItems:'start' }}>
        {/* Controls */}
        <div style={{ background:'var(--white)', borderRadius:'var(--r)', border:'1px solid var(--border)', padding:16, boxShadow:'var(--sh)' }}>
          <div className="f-row">
            <div className="f-lbl">Post Type</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:5 }}>
              {CON_TYPES.map(t => (
                <button key={t.id} onClick={() => { setSelType(t.id); setAngle(t.angles[0]); }}
                  style={{ padding:'7px 8px', borderRadius:7, cursor:'pointer', fontFamily:'inherit', textAlign:'left', fontSize:11, fontWeight:600,
                    border:`1.5px solid ${selType===t.id?'var(--rose)':'var(--border)'}`,
                    background:selType===t.id?'var(--rose)':'var(--fog)', color:selType===t.id?'#fff':'var(--bark)' }}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="f-row">
            <div className="f-lbl">Platform</div>
            <div style={{ display:'flex', gap:5 }}>
              {Object.entries(CON_PLATFORMS).map(([k,p]) => (
                <button key={k} onClick={() => setSelPlatform(k)}
                  style={{ flex:1, padding:'7px 5px', borderRadius:7, cursor:'pointer', fontFamily:'inherit', textAlign:'center', fontSize:10.5, fontWeight:600,
                    border:`1.5px solid ${selPlatform===k?p.color:'var(--border)'}`,
                    background:selPlatform===k?p.color:'var(--fog)', color:selPlatform===k?'#fff':'var(--bark)' }}>
                  {p.icon} {p.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize:10, color:'var(--sky-dk)', background:'var(--sky)', borderRadius:6, padding:'6px 9px', marginTop:7 }}>
              💡 {currentPlat.tip}
            </div>
          </div>

          <div className="f-row">
            <div className="f-lbl">Angle</div>
            <select className="f-sel" value={angle} onChange={e => setAngle(e.target.value)}>
              {currentType.angles.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div className="f-row">
            <div className="f-lbl">Context / Theme</div>
            <textarea className="f-ta" rows={2} placeholder="e.g. Valentine's Day, spring cold brew…"
              value={context} onChange={e => setContext(e.target.value)} />
          </div>

          <div className="f-row">
            <div className="f-lbl">Schedule Date</div>
            <input className="f-inp" type="date" value={schedDate} onChange={e => setSchedDate(e.target.value)} />
          </div>

          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-sm" style={{ flex:1, justifyContent:'center', background:'var(--rose)', color:'#fff', border:'none' }}
              onClick={generate} disabled={generating}>
              {generating ? <span className="dots"><i/><i/><i/></span> : '✨ Generate'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => { setContext(''); setSchedDate(''); setGenerated(null); }}>Clear</button>
          </div>
        </div>

        {/* Output */}
        <div>
          {!generated && !generating && (
            <div style={{ background:'var(--white)', borderRadius:'var(--r)', border:'1.5px dashed var(--border)', padding:32, textAlign:'center', color:'#9ca3af', fontSize:12 }}>
              <div style={{ fontSize:28, marginBottom:10 }}>✨</div>
              Select type and platform, then click <strong>Generate</strong>.
            </div>
          )}
          {generating && (
            <div style={{ background:'var(--white)', borderRadius:'var(--r)', border:'1px solid var(--border)', padding:24, boxShadow:'var(--sh)', display:'flex', alignItems:'center', gap:10, color:'#9ca3af', fontSize:12 }}>
              <span className="dots"><i/><i/><i/></span> Writing {currentType.label} for {currentPlat.label}…
            </div>
          )}
          {generated && (
            <div style={{ background:'var(--white)', borderRadius:'var(--r)', border:'1.5px solid var(--sage)', overflow:'hidden', boxShadow:'var(--sh)' }}>
              <div style={{ padding:'10px 14px', background:'var(--mist)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:14 }}>{currentType.icon}</span>
                <div>
                  <div style={{ fontSize:12.5, fontWeight:700 }}>{currentType.label} · {currentPlat.label}</div>
                  <div style={{ fontSize:10, color:'#6b7280' }}>{angle}</div>
                </div>
                <span style={{ marginLeft:'auto', fontSize:10, color:'#9ca3af' }}>{(generated.content||'').length} chars</span>
              </div>
              <div style={{ padding:16, maxHeight:400, overflowY:'auto', fontSize:13, lineHeight:1.7, whiteSpace:'pre-wrap' }}>
                {generated.content}
              </div>
              <div style={{ padding:'9px 14px', borderTop:'1px solid var(--border)', background:'var(--fog)', display:'flex', gap:7, flexWrap:'wrap' }}>
                <button className="btn btn-green btn-sm" onClick={() => navigator.clipboard.writeText(generated.content||'')}>📋 Copy</button>
                {selPlatform === 'instagram' && <button className="btn btn-outline btn-sm" onClick={() => window.open('https://www.instagram.com','_blank')}>📷 Instagram →</button>}
                {selPlatform === 'facebook'  && <button className="btn btn-outline btn-sm" onClick={() => window.open('https://www.facebook.com','_blank')}>👥 Facebook →</button>}
                <button className="btn btn-ghost btn-sm" onClick={regenerate}>↺ Regenerate</button>
                <button className="btn btn-ghost btn-sm" style={{ color:'var(--rose)' }} onClick={() => { removeConPost(generated.id).catch(console.error); setGenerated(null); }}>🗑</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent drafts */}
      {drafts.length > 0 && (
        <div style={{ marginTop:20, paddingTop:16, borderTop:'2px solid var(--border)' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:700, marginBottom:10 }}>Recent Drafts</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(250px,1fr))', gap:8 }}>
            {drafts.slice(0,6).map(p => (
              <div key={p.id} style={{ background:'var(--white)', borderRadius:'var(--r)', border:'1.5px solid var(--border)', overflow:'hidden', boxShadow:'var(--sh)' }}>
                <div style={{ padding:'8px 11px 6px', background:'var(--fog)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:6 }}>
                  <div style={{ flex:1, fontSize:11, fontWeight:700 }}>{p.title||p.type}</div>
                  <span style={{ fontSize:9, color:'#9ca3af' }}>{p.platform}</span>
                </div>
                <div style={{ padding:'8px 11px', fontSize:11, color:'#4b5563', lineHeight:1.5, maxHeight:60, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical' }}>
                  {p.content||''}
                </div>
                <div style={{ padding:'6px 11px', borderTop:'1px solid var(--border)', display:'flex', gap:5, background:'var(--fog)' }}>
                  <button className="btn btn-outline btn-xs" onClick={() => navigator.clipboard.writeText(p.content||'')}>📋</button>
                  <button className="btn btn-green btn-xs" onClick={() => updateConPost(p.id,{status:'published',published_at:new Date().toISOString()}).catch(console.error)}>✓ Publish</button>
                  <button className="btn btn-ghost btn-xs" style={{ color:'var(--rose)', marginLeft:'auto' }} onClick={() => removeConPost(p.id).catch(console.error)}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
